import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography, App, Alert } from "antd";
import {
  getPendingSlaInvitation,
  getSlaSignStatus,
} from "../../redux/slices/EsignSlice";
import CustomButton from "../../components/CustomButton/Button";
import Loader from "../../components/CustomLoader/Loader";
import { BTN_TXT } from "../../constants/constants";
import "./Sla.scss";

const { Text, Title } = Typography;

const Sla = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { pendingSla, pendingSlaLoading, checkStatusLoading } = useSelector(
    (state) => state.esign,
  );
  const [docData, setDocData] = useState({
    documentId: null,
    docketId: null,
  });
  const { documentId, docketId } = docData;

  useEffect(() => {
    dispatch(getPendingSlaInvitation());
  }, [dispatch]);

  useEffect(() => {
    if (pendingSla?.document_id && pendingSla?.docket_id) {
      setDocData((prev) => {
        if (
          prev.documentId === pendingSla.document_id &&
          prev.docketId === pendingSla.docket_id
        ) {
          return prev;
        }
        return {
          documentId: pendingSla.document_id,
          docketId: pendingSla.docket_id,
        };
      });
    }
  }, [pendingSla?.document_id, pendingSla?.docket_id]);

  const openAgreement = useCallback(
    (link) => {
      window.open(link, "_blank", "noopener,noreferrer");
      message.success(
        "SLA opened in a new tab. Please complete the signing process.",
      );
    },
    [message],
  );

  const signSlaRequest = async () => {
    try {
      const response = await dispatch(getPendingSlaInvitation()).unwrap();

      if (response?.isSigned) {
        message.success("SLA is already signed.");
        navigate("/questionnaire");
        return;
      }

      if (!response?.invitation_link) {
        message.warning(
          response?.isExpired
            ? "Your SLA signing link has expired. Please contact support or ask admin to resend."
            : "SLA has not been sent yet. Please wait for the admin to send the SLA.",
        );
        return;
      }

      setDocData({
        documentId: response.document_id,
        docketId: response.docket_id,
      });
      openAgreement(response.invitation_link);
    } catch {
      message.error("Failed to load SLA signing link.");
    }
  };

  const checkSlaStatus = useCallback(async () => {
    if (!documentId || !docketId) {
      return;
    }

    try {
      const { response } = await dispatch(
        getSlaSignStatus({ document_id: documentId, docket_id: docketId }),
      ).unwrap();

      if (response?.status === "signed") {
        message.success("SLA signed successfully!");
        dispatch(getPendingSlaInvitation());
        navigate("/questionnaire");
      } else if (response?.status === "unsigned") {
        message.warning(
          "SLA not signed yet. Please complete the signing process.",
        );
      }
    } catch {
      message.error("Failed to check SLA status.");
    }
  }, [dispatch, documentId, docketId, message, navigate]);

  useEffect(() => {
    const handleTabFocus = () => {
      if (document.visibilityState === "visible" && documentId && docketId) {
        dispatch(
          getSlaSignStatus({ document_id: documentId, docket_id: docketId }),
        )
          .unwrap()
          .then((result) => {
            if (result?.response?.status === "signed") {
              message.success("SLA signed successfully!");
              dispatch(getPendingSlaInvitation());
              navigate("/questionnaire");
            }
          })
          .catch(() => {
            // ignore focus-check errors
          });
      }
    };

    document.addEventListener("visibilitychange", handleTabFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleTabFocus);
    };
  }, [dispatch, documentId, docketId, message, navigate]);

  if (pendingSlaLoading && !pendingSla) {
    return <Loader />;
  }

  if (pendingSla?.isSigned) {
    return (
      <div className="sla-container">
        <div className="sla-content">
          <Title level={4}>Service Level Agreement (SLA)</Title>
          <Text type="secondary">Your SLA has already been signed.</Text>
          <CustomButton
            type="primary"
            style={{ marginTop: 16 }}
            onClick={() => navigate("/questionnaire")}
          >
            Continue
          </CustomButton>
        </div>
      </div>
    );
  }

  return (
    <div className="sla-container">
      <Alert
        message={
          <div>
            If your browser is blocking the signing page from opening in a new
            tab,
            <br />
            please allow pop-ups for this website and try again.
          </div>
        }
        type="info"
        showIcon
        className="info-alert"
      />

      {(checkStatusLoading || pendingSlaLoading) && <Loader />}

      <div className="sla-content">
        <Title level={4} style={{ marginBottom: 10 }}>
          Service Level Agreement (SLA)
        </Title>
        <Text type="secondary">
          {pendingSla?.isSent
            ? "Sign the SLA from here after login. You no longer need to open the email link."
            : "Waiting for admin to send the SLA. Once sent, you can sign it here without using email."}
        </Text>
        <CustomButton
          loading={pendingSlaLoading}
          type="primary"
          style={{ marginTop: 16 }}
          onClick={signSlaRequest}
          disabled={!pendingSla?.isSent && !pendingSlaLoading}
        >
          {BTN_TXT.SIGN_SLA}
        </CustomButton>
        {documentId && docketId && (
          <CustomButton
            style={{ marginTop: 12 }}
            loading={checkStatusLoading}
            onClick={checkSlaStatus}
          >
            Check Signing Status
          </CustomButton>
        )}
        <CustomButton
          style={{ marginTop: 16 }}
          type="link"
          onClick={() => navigate("/questionnaire")}
        >
          Maybe Later
        </CustomButton>
      </div>
    </div>
  );
};

export default Sla;
